import { startTransition, useCallback, useEffect, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { ExperiencePerformanceTypes, ExperienceTypes } from '@atlassian/ufo';
import { useUFOExperience } from '@atlassian/ufo-experimental';

import { QUICK_FIND_POST_QUERY_INTERACTION } from '../../../../common/constants/quick-find';
import {
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../../../../common/utils/events/vital-stats-events';
import { onSearchRequestCompleted } from '../../../../common/utils/quick-find/events/search-request-completed';
import { useAnalytics } from '../../../analytics';
import { useBootstrap, useIsHello, useSearchSessionId, useUserShardDebounceTimeout, useUserShardModel } from '../../../store/bootstrap';
import { usePrimaryProduct } from '../../../store/bootstrap/accessors';
import { useContent, useQuickFindOpen, useQuickFindQuery } from '../../../store/quick-find';
import { useSearchActions } from '../../../store/search';
import { useQuickFindAnalytics, useQuickFindAttributes } from '../../analytics';
import { useVitalStatsTaskState } from '../../quickfind-post-query-vitalstats-context';

export const DEFAULT_DEBOUNCE_TIMEOUT = 250;
export const DEBOUNCE_TIMEOUT = DEFAULT_DEBOUNCE_TIMEOUT;

export const useQuickFindDebounceTimeout = (): number => {
	const debounceTimeout = useUserShardDebounceTimeout();
	return debounceTimeout ?? DEFAULT_DEBOUNCE_TIMEOUT;
};

export const useQuickFindResultsCallbacks = () => {
	const [primaryProduct] = usePrimaryProduct();
	const serveUserShardModel = useUserShardModel();
	const { incrementQueryVersion } = useSearchActions();
	const [{ onSearchCompleted }] = useBootstrap();

	const { fireAnalyticsEvent: fireQuickFindAnalyticsEvent } = useQuickFindAnalytics();
	const { fireAnalyticsEvent: fireVitalStatsEvent } = useAnalytics();
	const [searchSessionId] = useSearchSessionId();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [isHello] = useIsHello();
	const quickFindOpen = useQuickFindOpen();
	const quickFindQuery = useQuickFindQuery();
	const content = useContent();
	const debounceTimeout = useQuickFindDebounceTimeout();

	// Store stable references for functions/values used in useEffect and useCallback
	// This avoids effects/callbacks re-running when analytics callbacks change,
	// while ensuring we always call the latest version via .current
	const fireVitalStatsEventRef = useRef(fireVitalStatsEvent);
	const searchSessionIdRef = useRef(searchSessionId);

	// State tracking for pre-query VitalStats events
	const preQueryTaskIdRef = useRef<string | null>(null);
	const preQueryTaskStartedRef = useRef<boolean>(false);
	const preQueryTaskCompletedRef = useRef<boolean>(false);
	const preQueryErrorRef = useRef<Error | null>(null);

	// State tracking for post-query VitalStats events using shared context
	const postQueryTaskState = useVitalStatsTaskState();

	// Store stable reference to postQueryTaskState to avoid adding it to dependency arrays
	const postQueryTaskStateRef = useRef(postQueryTaskState);

	// Track if we've handled cached state for this dialog session (postquery only)
	const handledCachedPostQueryStateRef = useRef<boolean>(false);

	// Track if we've handled cached state for prequery (when transitioning back from postquery)
	const handledCachedPreQueryStateRef = useRef<boolean>(false);

	// Track if we've handled initial dialog open with cached preQuery
	const handledInitialCachedPreQueryRef = useRef<boolean>(false);

	// Keep refs updated with latest values
	useEffect(() => {
		fireVitalStatsEventRef.current = fireVitalStatsEvent;
		searchSessionIdRef.current = searchSessionId;
		postQueryTaskStateRef.current = postQueryTaskState;
	}, [fireVitalStatsEvent, searchSessionId, postQueryTaskState]);

	// Handle initial dialog open with cached preQuery results
	// This fires VitalStats events when dialog opens with existing preQuery data
	useEffect(() => {
		// Reset flag when dialog closes
		if (!quickFindOpen) {
			handledInitialCachedPreQueryRef.current = false;
			return;
		}

		// Only fire once per dialog open session
		if (handledInitialCachedPreQueryRef.current) {
			return;
		}

		const state = content?.state;

		// Only fire if we have preQuery results showing but no task has started yet
		// This handles the case where dialog opens with cached preQuery results
		// Skip if we came from postQuery (that case is handled by the transition-back useEffect)
		if (state === 'PREQUERY_RESULTS' && !preQueryTaskStartedRef.current && !postQueryTaskStateRef.current.current.started) {
			handledInitialCachedPreQueryRef.current = true;

			// Use existing taskId if already set (e.g., by warmup request), otherwise generate new one
			if (!preQueryTaskIdRef.current) {
				preQueryTaskIdRef.current = crypto.randomUUID();
			}
			const taskId = preQueryTaskIdRef.current;
			preQueryTaskStartedRef.current = true;

			// Fire both taskStart and taskSuccess immediately (results are cached)
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskStart({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);

				fireVitalStatsEventRef.current(
					onTaskSuccess({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);
			});
			preQueryTaskCompletedRef.current = true;
		}
	}, [quickFindOpen, content?.state]);

	// Reset VitalStats state when dialog closes
	useEffect(() => {
		if (!quickFindOpen) {
			// Fire taskAbort if postQuery task is in progress
			if (postQueryTaskState.current.started && !postQueryTaskState.current.completed) {
				startTransition(() => {
					fireVitalStatsEventRef.current(
						onTaskAbort({
							searchSessionId: searchSessionIdRef.current,
							taskId: postQueryTaskState.current.id!,
							task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
							moreContext: { abortReason: 'dialog_closed_during_network' },
						}),
					);
				});
			}

			preQueryTaskStartedRef.current = false;
			preQueryTaskIdRef.current = null;
			preQueryTaskCompletedRef.current = false;
			preQueryErrorRef.current = null;
			postQueryTaskState.current.started = false;
			postQueryTaskState.current.id = null;
			postQueryTaskState.current.completed = false;
			// NOTE: We intentionally DO NOT clear postQueryTaskState.current.error here
			// so that when the dialog reopens with cached POSTQUERY_ERROR state,
			// we can still include error details in the taskFail event
			handledCachedPostQueryStateRef.current = false; // Reset for next open
			handledCachedPreQueryStateRef.current = false; // Reset for next open
			handledInitialCachedPreQueryRef.current = false; // Reset for next open
		}
	}, [quickFindOpen, postQueryTaskState]);

	// Fire taskSuccess/taskFail when content state changes
	useEffect(() => {
		if (!preQueryTaskStartedRef.current || preQueryTaskCompletedRef.current) {
			// Don't fire if task hasn't started or already completed
			return;
		}

		if (content?.state === 'PREQUERY_RESULTS') {
			// Task succeeds when PRE_QUERY results are shown
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskSuccess({
						searchSessionId: searchSessionIdRef.current,
						taskId: preQueryTaskIdRef.current!,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);
			});
			preQueryTaskCompletedRef.current = true;
		} else if (content?.state === 'PREQUERY_ERROR') {
			// Include error information if available (errorName and errorMessage for debugging)
			const moreContext: Record<string, unknown> | undefined = preQueryErrorRef.current
				? {
					errorName: preQueryErrorRef.current.name,
					errorMessage: preQueryErrorRef.current.message,
				}
				: undefined;

			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskFail({
						searchSessionId: searchSessionIdRef.current,
						taskId: preQueryTaskIdRef.current!,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
						moreContext,
					}),
				);
			});
			preQueryTaskCompletedRef.current = true;
		}
	}, [content?.state]);

	// Reset preQuery tracking when transitioning back from POSTQUERY to PREQUERY
	useEffect(() => {
		const state = content?.state;

		// If we're back in PREQUERY state after being in POSTQUERY, reset preQuery tracking
		// so that new preQuery VitalStats events can fire
		if (state === 'PREQUERY_RESULTS' || state === 'PREQUERY_ERROR' || state === 'PREQUERY_LOADING') {
			// Check if we previously had a postQuery task (indicates we're transitioning back)
			if (postQueryTaskStateRef.current.current.started) {
				// Abort the postQuery task if it's still active (not completed)
				// This handles the case where user backspaces/clears text before postQuery results arrive
				if (!postQueryTaskStateRef.current.current.completed) {
					startTransition(() => {
						fireVitalStatsEventRef.current(
							onTaskAbort({
								searchSessionId: searchSessionIdRef.current,
								taskId: postQueryTaskStateRef.current.current.id!,
								task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
								moreContext: { abortReason: 'text_cleared' },
							}),
						);
					});
					postQueryTaskStateRef.current.current.completed = true;
				}

				// Reset preQuery tracking flags to allow new preQuery task to start
				preQueryTaskStartedRef.current = false;
				preQueryTaskCompletedRef.current = false;
				preQueryTaskIdRef.current = null;
			}
		}
	}, [content?.state]);

	// Fire taskSuccess/taskFail for post-query when content state changes
	useEffect(() => {
		if (!postQueryTaskState.current.started || postQueryTaskState.current.completed) {
			// Don't fire if task hasn't started or already completed
			return;
		}

		const state = content?.state;

		if (state === 'POSTQUERY_RESULTS' || state === 'POSTQUERY_NO_RESULTS') {
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskSuccess({
						searchSessionId: searchSessionIdRef.current,
						taskId: postQueryTaskState.current.id!,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
					}),
				);
			});
			postQueryTaskState.current.completed = true;
		} else if (state === 'POSTQUERY_ERROR') {
			// Include error information if available (errorName and errorMessage for debugging)
			const moreContext: Record<string, unknown> | undefined = postQueryTaskState.current.error
				? {
					errorName: postQueryTaskState.current.error.name,
					errorMessage: postQueryTaskState.current.error.message,
				}
				: undefined;

			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskFail({
						searchSessionId: searchSessionIdRef.current,
						taskId: postQueryTaskState.current.id!,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						moreContext,
					}),
				);
			});
			postQueryTaskState.current.completed = true;
		}
	}, [content?.state, postQueryTaskState]);

	// Handle cached terminal states when dialog reopens
	// This fires VitalStats events for cached POSTQUERY_RESULTS/NO_RESULTS/ERROR states
	// when the dialog opens but no new fetch happens (e.g., same query as before)
	useEffect(() => {
		if (!quickFindOpen) {
			return;
		}

		// Only fire if task hasn't been started yet (dialog just opened)
		if (postQueryTaskState.current.started) {
			return;
		}

		// Only fire once per dialog open session
		if (handledCachedPostQueryStateRef.current) {
			return;
		}

		const state = content?.state;

		// Check if we have cached terminal postquery states
		if (state === 'POSTQUERY_RESULTS' || state === 'POSTQUERY_NO_RESULTS' || state === 'POSTQUERY_ERROR') {
			// Mark as handled to prevent duplicate events
			handledCachedPostQueryStateRef.current = true;

			// Start and immediately complete the task for the cached state
			const taskId = crypto.randomUUID();
			postQueryTaskState.current.id = taskId;
			postQueryTaskState.current.started = true;
			postQueryTaskState.current.completed = true;

			// Fire taskStart and completion events (defer to not block rendering)
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskStart({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
					}),
				);

				// Immediately fire completion event based on cached state
				if (state === 'POSTQUERY_RESULTS' || state === 'POSTQUERY_NO_RESULTS') {
					fireVitalStatsEventRef.current(
						onTaskSuccess({
							searchSessionId: searchSessionIdRef.current,
							taskId,
							task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						}),
					);
				} else if (state === 'POSTQUERY_ERROR') {
					// Note: error might be null since it was cleared on dialog close
					const moreContext: Record<string, unknown> | undefined = postQueryTaskState.current.error
						? {
							errorName: postQueryTaskState.current.error.name,
							errorMessage: postQueryTaskState.current.error.message,
						}
						: undefined;

					fireVitalStatsEventRef.current(
						onTaskFail({
							searchSessionId: searchSessionIdRef.current,
							taskId,
							task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
							moreContext,
						}),
					);
				}
			});

			// Clear error after it's been used to prevent memory accumulation
			if (state === 'POSTQUERY_ERROR') {
				postQueryTaskState.current.error = null;
			}
		}
	}, [quickFindOpen, content?.state, postQueryTaskState]);

	// Handle transition back to PREQUERY_RESULTS from postquery states
	// This fires VitalStats events when user clears the search text and returns to prequery
	useEffect(() => {
		const state = content?.state;

		// Reset the flag when entering postquery states so we can track the next transition back
		if (state === 'POSTQUERY_LOADING' || state === 'POSTQUERY_RESULTS' || state === 'POSTQUERY_NO_RESULTS' || state === 'POSTQUERY_ERROR') {
			handledCachedPreQueryStateRef.current = false;
			return;
		}

		// Only handle PREQUERY_RESULTS state
		if (state !== 'PREQUERY_RESULTS') {
			return;
		}

		// Don't fire if we've already handled this transition
		if (handledCachedPreQueryStateRef.current) {
			return;
		}

		// Only fire if we previously had a postquery task (meaning we're transitioning back from postquery)
		// We fire events even if preQueryCompleted is false because the results might be cached
		// (i.e., dialog opened with existing cached results, so onPreQueryStart was never called)
		if (postQueryTaskStateRef.current.current.started) {
			// Mark as handled to prevent duplicate events
			handledCachedPreQueryStateRef.current = true;

			// Reset prequery flags and start a new prequery task for the cached results
			// Use existing taskId if already set (e.g., by warmup request), otherwise generate new one
			if (!preQueryTaskIdRef.current) {
				preQueryTaskIdRef.current = crypto.randomUUID();
			}
			const taskId = preQueryTaskIdRef.current;
			preQueryTaskStartedRef.current = true;
			preQueryTaskCompletedRef.current = false; // Set to false first so the state-watching useEffect doesn't interfere

			// Fire taskStart and taskSuccess (defer to not block rendering)
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskStart({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);

				// Immediately fire taskSuccess for cached prequery results
				fireVitalStatsEventRef.current(
					onTaskSuccess({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);
			});

			// Now mark as completed
			preQueryTaskCompletedRef.current = true;
		}
	}, [content?.state]);

	// Delete useUFOExperience when cleaning up qf-remove-old-ufo-metric FG
	const {
		start: startPreQuery,
		success: successPreQuery,
		failure: failurePreQuery,
	} = useUFOExperience({
		id: `${primaryProduct}-pre-query-results-load`,
		component: 'search-dialog',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	// Delete useUFOExperience when cleaning up qf-remove-old-ufo-metric FG
	const {
		start: startPostQuery,
		success: successPostQuery,
		failure: failurePostQuery,
		abort: abortPostQuery,
	} = useUFOExperience({
		id: `${primaryProduct}-post-query-results-load`,
		component: 'search-dialog',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	/**
	 * Called when pre-query search starts.
	 * Fires VitalStats taskStart event to track the beginning of the pre-query operation.
	 * Only fires when Quick Find is actually open (not during background preloading).
	 * Ensures taskStart is only fired once per session.
	 */
	const onPreQueryStart = useCallback(() => {
		if (!fg('qf-remove-old-ufo-metric')) {
			startPreQuery();
		}

		// Fire VitalStats taskStart event only if Quick Find is open and not already started
		// Skip if cached POSTQUERY results are showing (postQuery events will handle that case)
		if (quickFindOpen && !preQueryTaskStartedRef.current) {
			// Check if we have cached postQuery results - if so, don't fire preQuery events
			const state = content?.state;

			if (state === 'POSTQUERY_RESULTS' || state === 'POSTQUERY_NO_RESULTS' || state === 'POSTQUERY_ERROR') {
				// Cached postQuery state detected - skip preQuery tracking
				// The cached postQuery useEffect will handle firing postQuery events
				return;
			}

			// Generate a consistent taskId for this pre-query task
			if (!preQueryTaskIdRef.current) {
				preQueryTaskIdRef.current = crypto.randomUUID();
			}

			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskStart({
						searchSessionId: searchSessionIdRef.current,
						taskId: preQueryTaskIdRef.current!,
						task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
					}),
				);
			});
			preQueryTaskStartedRef.current = true;

			// If cached preQuery data is already showing,
			// immediately fire taskSuccess since the useEffect won't trigger (no state change)
			if (content?.state === 'PREQUERY_RESULTS') {
				startTransition(() => {
					fireVitalStatsEventRef.current(
						onTaskSuccess({
							searchSessionId: searchSessionIdRef.current,
							taskId: preQueryTaskIdRef.current!,
							task: TASK_NAMES.QUICK_FIND_PRE_QUERY_RESULTS,
						}),
					);
				});
				preQueryTaskCompletedRef.current = true;
			}
		}
	}, [startPreQuery, quickFindOpen, content?.state]);

	/**
	 * Called when pre-query search completes successfully.
	 * VitalStats taskSuccess event is automatically fired by useEffect when content.state changes to PREQUERY_RESULTS.
	 */
	const onPreQuerySuccess = useCallback(() => {
		if (!fg('qf-remove-old-ufo-metric')) {
			successPreQuery({
				metadata: { isQuickFind: true },
			});
		}
		// VitalStats success event is handled by useEffect watching content.state
	}, [successPreQuery]);

	/**
	 * Called when pre-query search fails with an error.
	 * Stores error details (name, message) which will be included in the VitalStats taskFail event.
	 * VitalStats taskFail event is automatically fired by useEffect when content.state changes to PREQUERY_ERROR.
	 *
	 * @param error - Optional Error object containing failure details. Error messages should not contain PII.
	 */
	const onPreQueryFailure = useCallback(
		(error?: Error) => {
			if (!fg('qf-remove-old-ufo-metric')) {
				failurePreQuery();
			}
			// Store error for useEffect to access
			if (error) {
				preQueryErrorRef.current = error;
			}
			// VitalStats fail event is handled by useEffect watching content.state
		},
		[failurePreQuery],
	);

	const onPostQueryStart = useCallback(() => {
		// If dialog is closed, fire taskStart + immediate taskAbort to track background cache request
		// Network request still fires for caching, but user isn't waiting for it
		if (!quickFindOpen) {
			// Generate taskId for the background cache request
			const taskId = crypto.randomUUID();

			postQueryTaskState.current.id = taskId;
			postQueryTaskState.current.started = true;
			postQueryTaskState.current.completed = true; // Mark as completed immediately

			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskStart({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
					}),
				);

				fireVitalStatsEventRef.current(
					onTaskAbort({
						searchSessionId: searchSessionIdRef.current,
						taskId,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						moreContext: { abortReason: 'dialog_closed_before_debounce_fired' },
					}),
				);
			});

			return;
		}

		// Abort previous task if still active
		if (postQueryTaskState.current.started && !postQueryTaskState.current.completed) {
			startTransition(() => {
				fireVitalStatsEventRef.current(
					onTaskAbort({
						searchSessionId: searchSessionIdRef.current,
						taskId: postQueryTaskState.current.id!,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						moreContext: { abortReason: 'superseded_by_new_query' },
					}),
				);
			});
		}

		// Start new task - use existing taskId if already set (e.g., by fetchQuickFindPostQuery)
		// Only generate a new one if not already set or if we just aborted a previous task
		if (!postQueryTaskState.current.id || postQueryTaskState.current.started) {
			postQueryTaskState.current.id = crypto.randomUUID();
		}
		const taskId = postQueryTaskState.current.id;
		postQueryTaskState.current.started = true;
		postQueryTaskState.current.completed = false;

		startTransition(() => {
			fireVitalStatsEventRef.current(
				onTaskStart({
					searchSessionId: searchSessionIdRef.current,
					taskId,
					task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
				}),
			);
		});

		/**
		 * QS-7380 (LDR: https://hello.atlassian.net/wiki/x/QVo8QwE)
		 * Aborting on keypress fires a lot of dirty data volume to ufo. Instead we only instrument on query (inside the debounce), offsetting the start time by the debounce window because the keypress that trigger the debounce is the start of the interaction.
		 */
		const startTimestamp = performance.now() - debounceTimeout;
		if (fg('quick_find_metrics_cancel_postquery_on_close')) {
			// Only begin the interaction if QF is still open
			// If QF is closed, we still make the network request, so the next open of the same text is instant
			// However, we must not start the interaction, as if QF is closed during the debounce window, this interaction can abort other intreractions, like FPS search results shown!
			if (quickFindOpen) {
				traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION, startTimestamp);
			}
		} else {
			traceUFOPress(QUICK_FIND_POST_QUERY_INTERACTION, startTimestamp);
		}

		// ufo-v1 (above is react-ufo)
		const restartMetric = async () => {
			if (!fg('qf-remove-old-ufo-metric')) {
				await abortPostQuery();
				if (fg('quick_find_metrics_cancel_postquery_on_close')) {
					if (quickFindOpen) {
						await startPostQuery();
					}
				} else {
					await startPostQuery();
				}
			}
		};
		restartMetric();
	}, [abortPostQuery, startPostQuery, quickFindOpen, debounceTimeout, postQueryTaskState]);

	const onPostQuerySuccess = useCallback(
		(duration: number, hasThirdPartyResults: boolean, resultCount?: number) => {
			incrementQueryVersion();
			onSearchCompleted?.(resultCount, quickFindQuery);

			if (!fg('qf-remove-old-ufo-metric')) {
				successPostQuery({
					metadata: {
						isQuickFind: true,
						isHello,
						use3PResults: serveUserShardModel,
						hasThirdPartyResults,
					},
				});
			}

			fireQuickFindAnalyticsEvent(
				onSearchRequestCompleted({
					attributes: {
						...commonAttributes,
						postQueryRequestDurationMs: duration,
						hasThirdPartyResults,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireQuickFindAnalyticsEvent,
			incrementQueryVersion,
			nonPrivacySafeAttributes,
			isHello,
			serveUserShardModel,
			successPostQuery,
			onSearchCompleted,
			quickFindQuery,
		],
	);

	const onPostQueryFailure = useCallback(
		(error?: Error) => {
			if (!fg('qf-remove-old-ufo-metric')) {
				failurePostQuery();
			}

			setInteractionError(QUICK_FIND_POST_QUERY_INTERACTION, {
				errorMessage: error?.message || 'request failed',
				name: error?.name || 'request failed',
			});

			// Store error for useEffect to access when state changes to POSTQUERY_ERROR
			if (error) {
				postQueryTaskState.current.error = error;
			}
		},
		[failurePostQuery, postQueryTaskState],
	);

	const onPostQueryCancel = useCallback(() => {
		if (!fg('qf-remove-old-ufo-metric')) {
			abortPostQuery();
		}
	}, [abortPostQuery]);

	return useMemo(
		() => ({
			onPreQueryStart,
			onPreQuerySuccess,
			onPreQueryFailure,
			onPostQueryStart,
			onPostQuerySuccess,
			onPostQueryFailure,
			onPostQueryCancel,
			postQueryTaskState,
			preQueryTaskIdRef,
		}),
		[
			onPostQueryCancel,
			onPostQueryFailure,
			onPostQueryStart,
			onPostQuerySuccess,
			onPreQueryFailure,
			onPreQueryStart,
			onPreQuerySuccess,
			postQueryTaskState,
		],
	);
};
